import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

export default function ClientForm() {
  const { slug } = useParams();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [inputErrors, setInputErrors] = useState({ name: false, email: false });
  const [progress, setProgress] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);

  const nameRef = useRef(null);
  const emailRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(
          `https://klrochdale1.azure-api.net/azngetguidklrhmrc1/GetClientData?slug=${slug}`
        );

        const data = await res.json();

        if (!res.ok) throw new Error(`Function error: ${res.status}`);
        if (!data || !data.employers) throw new Error("No data returned for this slug");

        setFormData({
          name: "",
          email: "",
          caseRef: data.caseRef,
          employers: data.employers.map((emp) => ({
            name: emp,
            jobTitle: "",
            noise: false,
            protection: false
          }))
        });

        setLoading(false);
      } catch (err) {
        console.error("Error loading client data:", err);
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  useEffect(() => {
    if (!formData) return;

    const total = formData.employers.length;
    const completed = formData.employers.filter(
      (emp) => emp.jobTitle.trim() !== "" && (emp.noise || emp.protection)
    ).length;

    const percent = Math.round((completed / total) * 100);
    setProgress(percent);
    setCompletedCount(completed);

    if (completed > 0 && navigator.vibrate) {
      navigator.vibrate(100);
    }
  }, [formData]);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setInputErrors({ ...inputErrors, [field]: false });
  };

  const handleEmployerChange = (index, key, value) => {
    const updated = [...formData.employers];
    updated[index][key] = value;
    setFormData({ ...formData, employers: updated });
  };

  const handleSubmit = async () => {
    const errors = {
      name: !formData.name.trim(),
      email: !formData.email.trim()
    };
    setInputErrors(errors);

    if (errors.name) {
      nameRef.current?.focus();
      return;
    }
    if (errors.email) {
      emailRef.current?.focus();
      return;
    }

    try {
      const response = await fetch("https://klrochdale1.azure-api.net/submit-form", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error("Flow call failed");

      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert("Submission failed. Please try again.");
    }
  };

  const getProgressColor = () => {
    if (progress < 50) return "bg-red-500";
    if (progress < 100) return "bg-yellow-500";
    return "bg-green-500";
  };

  /*if (loading) return <div className="p-10 text-center">Loading form...</div>;*/
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <svg
            className="animate-spin h-10 w-10 text-purple-600 mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8H4z"
            />
          </svg>
          <p className="text-gray-700 text-lg mb-2">
            Just a moment while we load your form...
          </p>
          <p className="text-sm text-gray-500">
            This may take a few seconds. Please stay on this page.
          </p>
        </div>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="p-10 text-center text-red-500">
        This link may have expired or is invalid.
        <br />
        <a href="/" className="text-blue-600 underline mt-4 inline-block">Return to homepage</a>
      </div>
    );
  }
  if (submitted)
    return (
      <div className="p-10 text-center text-green-600">
        Thank you! Your form has been submitted.
      </div>
    );

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Sticky Progress Bar */}
      <div className="sticky top-0 z-50 bg-white shadow p-3">
        <div className="text-sm text-gray-600 mb-1">
          Progress: {completedCount} of {formData.employers.length} employers completed ({progress}%)
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className={`${getProgressColor()} h-3 rounded-full transition-all duration-300`}
            style={{ width: `${progress}%` }}
          />
        </div>
        {progress === 100 && (
          <div className="text-green-600 text-sm mt-2 flex items-center justify-center">🎉 All employers completed!</div>
        )}
      </div>

      <main className="flex-grow py-10 px-4">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md p-6">
          <div className="flex justify-center mb-6">
            <img
              src="/images/kineticlaw-logo.png"
              alt="Kinetic Law Logo"
              className="h-12 md:h-16 object-contain"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </div>

          <h1 className="text-3xl font-bold text-center text-[#93268F] mb-6">
            Employment History Confirmation
          </h1>

          <p className="text-center text-gray-600 mb-8">
            Please confirm your employment history for case <strong>{formData.caseRef}</strong>.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div>
              <input
                ref={nameRef}
                className={`border rounded px-3 py-2 w-full ${inputErrors.name ? "border-red-500" : "border-gray-300"}`}
                placeholder="Full Name (required)"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                required
              />
              {inputErrors.name && <p className="text-red-500 text-sm mt-1">This field is required.</p>}
            </div>
            <div>
              <input
                ref={emailRef}
                className={`border rounded px-3 py-2 w-full ${inputErrors.email ? "border-red-500" : "border-gray-300"}`}
                placeholder="Email Address (required)"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                required
              />
              {inputErrors.email && <p className="text-red-500 text-sm mt-1">This field is required.</p>}
            </div>
          </div>

          <div className="space-y-6">
            {formData.employers.map((emp, index) => (
              <div
                key={index}
                className="border border-gray-200 p-4 rounded-lg bg-gray-50 shadow-sm transition duration-200 hover:shadow-md hover:border-blue-400"
              >
                <p className="font-semibold text-[#93268F]">{index + 1}. {emp.name}</p>
                <div className="mt-2">
                  <textarea
                    className="w-full border border-gray-300 px-3 py-2 rounded"
                    placeholder="Job title or brief description"
                    value={emp.jobTitle}
                    onChange={(e) => handleEmployerChange(index, "jobTitle", e.target.value)}
                  />
                </div>

                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="flex flex-col items-center text-center">
                    <span className="text-sm font-medium text-gray-700 mb-1">Loud Noise?</span>
                    <CustomCheckbox
                      id={`noise-${index}`}
                      checked={emp.noise}
                      onChange={(e) => handleEmployerChange(index, "noise", e.target.checked)}
                    />
                    <span className="text-xs text-gray-500 mt-1">{isMobile ? "Tap" : "Click"} the box for Yes</span>
                  </div>
                  <div className="flex flex-col items-center text-center">
                    <span className="text-sm font-medium text-gray-700 mb-1">Ear Protection?</span>
                    <CustomCheckbox
                      id={`protection-${index}`}
                      checked={emp.protection}
                      onChange={(e) => handleEmployerChange(index, "protection", e.target.checked)}
                    />
                    <span className="text-xs text-gray-500 mt-1">{isMobile ? "Tap" : "Click"} the box for Yes</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-right">
            <button
              className="bg-[#9B51E0] text-white px-6 py-2 rounded hover:bg-[#7a3fc2] transition"
              onClick={handleSubmit}
            >
              Submit
            </button>
          </div>
        </div>
      </main>

      <footer className="border-t text-center text-sm text-gray-500 py-6 bg-white">
        <p className="font-semibold text-[#93268F]">Kinetic Law</p>
        <p>
          <a
            href="https://kineticlaw.co.uk"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#9B51E0] hover:underline"
          >
            https://kineticlaw.co.uk
          </a>
        </p>
        <p>Telephone: 01706 254345</p>
      </footer>
    </div>
  );
}

function CustomCheckbox({ id, checked, onChange }) {
  return (
    <label htmlFor={id} className="relative cursor-pointer inline-block w-10 h-10">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="appearance-none w-10 h-10 bg-gray-200 border border-gray-300 rounded-md checked:bg-blue-500 checked:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-transform duration-200 ease-in-out active:scale-90"
      />
      {checked && (
        <svg
          className="absolute left-0 top-0 w-10 h-10 p-2 text-white pointer-events-none animate-ping-fast"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )}
    </label>
  );
}
